import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth'; 
import { auth } from './firebaseConfig';
import "./i18n";

import LoadingPage from './components/LoadingPage';
import NotFound from "./pages/NotFound";

// Landing page import
import LandingPage from './pages/Landing/LandingPage.js';
import TutorSamah from './pages/TutorSamah.js';
import SubmitPaymentReceiptPage from './pages/SubmitPaymentReceiptPage.js';
import PublicBlogList from './pages/PublicBlogList.js';
import PublicBlogPost from './pages/PublicBlogPost.js';

// Admin' pages import 
import { AdminDataProvider } from "./context/AdminDataContext";
import { AdminAccessProvider, useAdminAccess } from "./context/AdminAccessContext";
import AdminRegister from './pages/AdminRegister';
import AdminLogin from './pages/AdminLogin';
import AdminHome from './pages/AdminHome';
import AdminAllStudents from './pages/AdminAllStudents';
import AdminAllTeachers from './pages/AdminAllTeachers';
import AdminAllAdmins from './pages/AdminAllAdmins';
import AdminAnalytics from './pages/AdminAnalytics.js';
import AdminBilling from './pages/AdminBilling.js';
import AdminContactUs from './pages/AdminContactUs.js';
import AdminBlogManagement from './pages/AdminBlogManagement.js';
import AdminBlogEditor from './pages/AdminBlogEditor.js';
import AdminKnowledgeBase from './pages/AdminKnowledgeBase.js';


// Teachers' pages import 
import TeacherLogin from './pages/TeacherLogin.js';
import TeacherClasses from './pages/TeacherClasses.js';
import TeacherClass from './pages/TeacherClass.js';
import TeacherForum from './pages/TeacherForum.js';
import TeacherChatPage from "./pages/TeacherChatPage";
import TeacherContactUs from './pages/TeacherContactUs.js';
import TeacherTwitchStreamDisplay from './components/twitch/TeacherTwitchStreamDisplay';
import Lesson from './components/lesson';
import TeacherStudentsPage from "./pages/TeacherStudentsPage";
import TeacherGroups from "./pages/TeacherGroupsPage.js";
import TeacherServicesPage from "./pages/TeacherServicesPage.js";
import TeacherExercises from "./pages/TeacherExercises.js";

// Students' pages import 
import StudentLogin from './pages/StudentLogin.js';
import StudentJoinClass from './pages/StudentJoinClass';
import StudentClasses from './pages/StudentClasses.js';
import StudentClass from './pages/StudentClass.js';
import StudentChatPage from "./pages/StudentChatPage";
import StudentContactUs from './pages/StudentContactUs.js';
import StudentTwitchStreamDisplay from './components/twitch/StudentTwitchStreamDisplay';
import StudentExercises from './pages/StudentExercises.js';
import StudentLayout from './components/student/StudentLayout.js';

// The student forum is disabled — any direct link sends the student back
// to their class page instead of a broken/unreachable feature.
function RedirectToStudentClass() {
  const { groupId } = useParams();
  return <Navigate to={`/student/class/${groupId}`} replace />;
}

function App() {
    function MarketingAccessBoundary({ children }) {
      const location = useLocation();
      const { isMarketing, loading } = useAdminAccess();

      // Keep the public join form mounted while registration changes auth state.
      // The invitation API independently checks the student's account and access.
      if (location.pathname === '/student/join' || location.pathname.startsWith('/student/join/')) {
        return children;
      }

      if (loading) {
        return <LoadingPage />;
      }

      if (isMarketing && !location.pathname.startsWith("/06071966/blogs")) {
        return <Navigate to="/06071966/blogs" replace />;
      }

      return children;
    }

    // Private Route for ADMINS
    function AdminPrivateRoute({ children, allowMarketing = false }) {
      const { user, adminRecord, isMarketing, loading } = useAdminAccess();
    
      if (loading) {
         return <LoadingPage />;
      }
    
      if (!user) {
         return <Navigate to="/06071966/login" replace />;
      }

      if (!adminRecord) {
        return <Navigate to="/06071966/login" replace />;
      }

      if (isMarketing && !allowMarketing) {
        return <Navigate to="/06071966/blogs" replace />;
      }
    
       return children; 
    }

  // Private Route for TEACHERS
  function TeacherPrivateRoute({ children }) {
    const [user, loading] = useAuthState(auth); 
  
    if (loading) {
      return <LoadingPage />;
     }
  
    if (!user) {
       return <Navigate to="/teacher/login" replace />;
    }
  
     return children; 
  }


  // Private Route for STUDENTS
  function StudentPrivateRoute({ children }) {
    const [user, loading] = useAuthState(auth); 

     if (loading) {
      return <LoadingPage />;
     }

     if (!user) {
      return <Navigate to="/student/login" replace />;
   }

    return children; 
  }


  return (
    <div className="App">
      <AdminAccessProvider>
      <AdminDataProvider>
      <MarketingAccessBoundary>
      <Routes>
        <Route path="/student/join" element={<StudentJoinClass />} />
        <Route path="/student/join/:token" element={<StudentJoinClass />} />

      <Route 
          path="/student/class/:groupId/live" 
          element={
            <StudentPrivateRoute>
              <StudentLayout>
                <StudentTwitchStreamDisplay />
              </StudentLayout>
            </StudentPrivateRoute>
          } 
        />

        <Route 
          path="/teacher/class/:groupId/live" 
          element={
            <TeacherPrivateRoute>
              <TeacherTwitchStreamDisplay />
            </TeacherPrivateRoute>
          } 
        />

        <Route path="/" element={<LandingPage />} />
        <Route path="/blogs" element={<PublicBlogList />} />
        <Route path="/blogs/:slug" element={<PublicBlogPost />} />
        <Route path="/blog" element={<Navigate to="/blogs" replace />} />
        <Route path="/blog/:slug" element={<PublicBlogPost />} />
        <Route path="/tutor/be_healthy_with_samah" element={<TutorSamah />} />
        <Route path="/tutor/:teacherUid/:intakeId/submit-payment-receipt" element={<SubmitPaymentReceiptPage />} />
        <Route path="*" element={<NotFound />} />


        <Route path="/06071966/register" element={<AdminRegister />} />
        <Route path="/06071966/login" element={<AdminLogin />} /> 
        {/*<Route path="/06071966/home" element={<AdminPrivateRoute> <AdminHome /> </AdminPrivateRoute> }/> */}
        <Route path="/06071966/AdminAllStudents" element={<AdminPrivateRoute> <AdminAllStudents /> </AdminPrivateRoute>} />
        <Route path="/06071966/AdminAllTeachers" element={<AdminPrivateRoute> <AdminAllTeachers /> </AdminPrivateRoute>} />
        <Route path="/06071966/AdminAllAdmins" element={<AdminPrivateRoute> <AdminAllAdmins /> </AdminPrivateRoute>} />
        <Route path="/06071966/analytics" element={<AdminPrivateRoute> <AdminAnalytics /> </AdminPrivateRoute> }/>
        <Route path="/06071966/knowledge-base" element={<AdminPrivateRoute> <AdminKnowledgeBase /> </AdminPrivateRoute> }/>
        {/*<Route path="/06071966/billing" element={<AdminPrivateRoute> <AdminBilling /> </AdminPrivateRoute> }/> */}
        <Route path="/06071966/contactUs" element={<AdminPrivateRoute> <AdminContactUs /> </AdminPrivateRoute> }/> 
        <Route path="/06071966/blogs" element={<AdminPrivateRoute allowMarketing> <AdminBlogManagement /> </AdminPrivateRoute>} />
        <Route path="/06071966/blogs/new" element={<AdminPrivateRoute allowMarketing> <AdminBlogEditor /> </AdminPrivateRoute>} />
        <Route path="/06071966/blogs/edit/:blogId" element={<AdminPrivateRoute allowMarketing> <AdminBlogEditor /> </AdminPrivateRoute>} />


        {/* Teachers' side routes */}
        <Route path="/teacher/login" element={<TeacherLogin />} />
        <Route path="/teacher/classes" element={<TeacherPrivateRoute> <TeacherClasses /> </TeacherPrivateRoute> }/> 
        <Route path="/teacher/class/:groupId" element={<TeacherPrivateRoute> <TeacherClass /> </TeacherPrivateRoute>} /> 
        <Route path="/teacher/class/:groupId/lesson" element={<Lesson />} />
        <Route path="/teacher/class/:groupId/forum" element={<TeacherPrivateRoute> <TeacherForum /> </TeacherPrivateRoute>} />
        <Route path="/teacher/class/:groupId/exercises" element={<TeacherPrivateRoute> <TeacherExercises /> </TeacherPrivateRoute>} />
        <Route path="/teacher/chat" element={<TeacherPrivateRoute> <TeacherChatPage /> </TeacherPrivateRoute>} />
        <Route path="/teacher/contactUs" element={<TeacherPrivateRoute> <TeacherContactUs /> </TeacherPrivateRoute>} />
        <Route path="/teacher/students" element={<TeacherStudentsPage />} />
        <Route path="/teacher/groups" element={<TeacherGroups />} />
        <Route path="/teacher/services" element={<TeacherPrivateRoute> <TeacherServicesPage /> </TeacherPrivateRoute>} />

        {/* Students' side routes */}
        <Route path="/student/login" element={<StudentLogin />} />
        <Route path="/student/classes" element={<StudentPrivateRoute> <StudentClasses /> </StudentPrivateRoute> }/>
        <Route path="/student/class/:groupId" element={<StudentPrivateRoute> <StudentClass /> </StudentPrivateRoute> }/>
        <Route
          path="/student/class/:groupId/lesson"
          element={
            <StudentPrivateRoute>
              <StudentLayout>
                <Lesson chrome="none" roleOverride="student" />
              </StudentLayout>
            </StudentPrivateRoute>
          }
        />
        <Route path="/student/class/:groupId/forum" element={<StudentPrivateRoute> <RedirectToStudentClass /> </StudentPrivateRoute> }/>
        <Route path="/student/class/:groupId/exercises" element={<StudentPrivateRoute> <StudentExercises /> </StudentPrivateRoute> }/>
        <Route path="/student/chat" element={<StudentPrivateRoute> <StudentChatPage /> </StudentPrivateRoute>} />
        <Route path="/student/contactUs" element={<StudentPrivateRoute> <StudentContactUs/> </StudentPrivateRoute> }/>

      </Routes>
      </MarketingAccessBoundary>
      </AdminDataProvider>
      </AdminAccessProvider>
    </div>
  );
}

export default App;
